import { toast } from "sonner";
import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { cassa, getCassaToken, onCassaToken, setCassaToken } from "./cassa/cassaApi";
import { CassaGate } from "./cassa/CassaGate";
import { SaleTab, newDraft } from "./cassa/SaleTab";
import { QuotesTab, quoteToDraft } from "./cassa/QuotesTab";
import { ReturnsTab, VouchersTab } from "./cassa/VouchersTab";
import { ProductsTab } from "./cassa/ProductsTab";
import { ClientsTab } from "./cassa/ClientsTab";
import { InvoicesTab } from "./cassa/InvoicesTab";
import { SettingsTab } from "./cassa/SettingsTab";
import { ReceiptsTab } from "./cassa/ReceiptsTab";
import { StatsTab } from "./cassa/StatsTab";

const TABS = [["sale", "Vendita"], ["products", "Articoli"], ["clients", "Clienti"], ["invoices", "Fatture"], ["receipts", "Ricevute"], ["quotes", "Preventivi"], ["vouchers", "Buoni"], ["returns", "Resi"], ["stats", "Statistiche"], ["settings", "Impostazioni"]];
const IDLE_MS = 15 * 60 * 1000;

function useIdleLock(active) {
  useEffect(() => {
    if (!active) return;
    let t;
    const reset = () => { clearTimeout(t); t = setTimeout(() => { cassa.post("/lock").catch(() => {}); setCassaToken(null); }, IDLE_MS); };
    const ev = ["mousemove", "keydown", "click", "touchstart", "scroll"];
    ev.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => { clearTimeout(t); ev.forEach((e) => window.removeEventListener(e, reset)); };
  }, [active]);
}

export function AdminCassa() {
  const [tok, setTok] = useState(getCassaToken());
  const [tab, setTab] = useState("sale");
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState(newDraft());
  useEffect(() => onCassaToken(setTok), []);
  useEffect(() => () => { cassa.post("/lock").catch(() => {}); setCassaToken(null); }, []);
  useEffect(() => { if (tok) cassa.get("/invoice-settings").then((r) => { setSettings(r.data); setDraft((d) => (d.lines.length ? d : newDraft(r.data))); }).catch(() => {}); }, [tok]);
  useIdleLock(!!tok);
  if (!tok) return <CassaGate />;
  const lock = () => { cassa.post("/lock").catch(() => {}); setCassaToken(null); };
  return (
    <div className="space-y-5" data-testid="admin-cassa">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><div className="font-mono text-[11px] tracking-widest text-slate-500">AREA RISERVATA</div><h1 className="text-4xl font-extrabold uppercase">Cassa</h1></div>
        <button data-testid="cassa-fullscreen-btn" onClick={() => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen?.())} className="press h-10 px-3 rounded-md border bg-white text-sm font-semibold">Schermo intero</button>
        <button data-testid="cassa-lock-btn" onClick={lock} className="press h-10 px-4 rounded-md border border-brand-navy text-brand-navy text-sm font-semibold flex items-center gap-2"><Lock className="w-4 h-4" /> Blocca cassa</button>
      </div>
      <div className="flex gap-1 overflow-x-auto border-b">
        {TABS.map(([k, l]) => <button key={k} data-testid={`cassa-tab-${k}`} onClick={() => setTab(k)} className={`press h-11 px-4 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${tab === k ? "border-brand-yellow text-brand-navy" : "border-transparent text-slate-500"}`}>{l}</button>)}
      </div>
      {tab === "sale" && <SaleTab draft={draft} setDraft={setDraft} settings={settings} />}
      {tab === "products" && <ProductsTab />}
      {tab === "clients" && <ClientsTab />}
      {tab === "invoices" && <InvoicesTab />}
      {tab === "receipts" && <ReceiptsTab />}
      {tab === "quotes" && <QuotesTab onLoad={(q) => { setDraft(quoteToDraft(q, settings)); setTab("sale"); toast.success(`Preventivo ${q.number} caricato in Vendita`); }} />}
      {tab === "vouchers" && <VouchersTab />}
      {tab === "returns" && <ReturnsTab />}
      {tab === "stats" && <StatsTab />}
      {tab === "settings" && <SettingsTab settings={settings} onSaved={setSettings} />}
    </div>
  );
}
